import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CrimsonLogo from '../assets/logos/crimson-logo-shield.svg';

const highlights = [
  {
    value: 'Global network',
    label: 'Students, alumni, and allies collaborating across Mexico and abroad.',
  },
  {
    value: 'Year-round programming',
    label: 'Initiatives spanning civic leadership, culture, and innovation.',
  },
  {
    value: 'Independent & non-profit',
    label:
      'CCM is led by Mexican students, alumni, and professionals. Opinions shared at our events reflect each participant\'s personal views.',
  },
];

const Home = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const handleSmoothScroll = (event, targetId) => {
    event.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: 'common.white',
        minHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 72px)' },
        display: 'flex',
        alignItems: 'center',
        py: { xs: 10, md: 14 },
        bgcolor: 'secondary.main',
        background: (t) =>
          `linear-gradient(135deg, rgba(14,14,14,0.92) 0%, rgba(165,0,52,0.88) 60%, ${t.palette.primary.main} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 340,
          height: 340,
          borderRadius: '50%',
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.15), transparent 65%)',
          top: -80,
          right: -120,
          filter: 'blur(0.5px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.1), transparent 60%)',
          bottom: -120,
          left: -80,
        },
      }}
    >
      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={3} textAlign={isMdDown ? 'center' : 'left'}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ letterSpacing: 6, textTransform: 'uppercase' }}>
                  Crimson Club of Mexico
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.8rem', sm: '3.2rem', md: '3.8rem' },
                    lineHeight: 1.1,
                  }}
                >
                  Voices creating impact across Mexico.
                </Typography>
                <Typography
                  variant="body1"
                  color="rgba(255,255,255,0.78)"
                  sx={{ maxWidth: 560, mx: isMdDown ? 'auto' : 0 }}
                >
                  We connect students and alumni to collaborate on civic, cultural, and professional
                  initiatives that move our country forward.
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent={isMdDown ? 'center' : 'flex-start'}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  href="#events"
                  onClick={(event) => handleSmoothScroll(event, 'events')}
                  sx={{
                    px: { xs: 4, sm: 5 },
                    py: 1.5,
                    borderRadius: 999,
                    boxShadow: '0 12px 24px rgba(165,0,52,0.35)',
                    background: 'linear-gradient(135deg, #c40042 0%, #a50034 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d61051 0%, #b0003a 100%)',
                      boxShadow: '0 14px 28px rgba(165,0,52,0.4)',
                    },
                  }}
                >
                  Explore upcoming events
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  href="mailto:contact-us@crimsonclub.mx"
                >
                  Partner with us
                </Button>
              </Stack>

              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{
                  mx: { xs: 'auto', sm: 0 },
                  width: '100%',
                  maxWidth: { xs: 360, sm: 'none' },
                }}
              >
                {highlights.map((item) => (
                  <Grid item xs={12} sm={4} key={item.label}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        height: '100%',
                        width: '100%',
                        maxWidth: { xs: 360, sm: 'none' },
                        mx: 'auto',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(6px)',
                        textAlign: isMdDown ? 'center' : 'left',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        color="common.white"
                        sx={{ fontWeight: 700 }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        {item.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={8}
              sx={{
                width: '100%',
                maxWidth: 360,
                mx: { xs: 'auto', md: 'unset' },
                p: { xs: 5, md: 6 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '32px',
                background:
                  'radial-gradient(circle at top, rgba(255,255,255,0.2), rgba(255,255,255,0.05) 55%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Box
                component="img"
                src={CrimsonLogo}
                alt="Crimson Club shield"
                sx={{
                  width: { xs: '65%', sm: '70%', md: '78%' },
                  minWidth: 220,
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
